import { Navigate, Outlet, useNavigate } from 'react-router-dom';
import { Suspense, useCallback, useEffect } from 'react';
import { User } from 'oidc-client-ts';
import { AuthProvider, useAuth } from 'react-oidc-context';
import { isLegacyUrl } from '../Support';
import { getOAuthConfig, PageLoader, RaygunTracker } from '@diana/core';
import { CoreRouterContextConnector } from './CoreRouterContextConnector';
import { useAppEvents } from '../../hooks/useAppEvents';

function NoMatchingStateErrorWatcher() {
  const { isAuthenticated, isLoading, error } = useAuth();

  useEffect(() => {
    if (
      error?.message.toLowerCase() === 'no matching state found in storage' &&
      !isAuthenticated &&
      !isLoading
    ) {
      window.location.href = window.location.origin;
    }
  }, [error?.message, isAuthenticated, isLoading]);

  return null;
}

export function Bootstrap() {
  const navigate = useNavigate();
  const onAuthSignInCallback = useCallback(
    (user: User | void) => {
      if (!user) return;
      const state = user.state as undefined | { from: string };
      navigate(state?.from || '/', { replace: true });
    },
    [navigate],
  );

  useAppEvents();

  if (isLegacyUrl()) {
    return <Navigate to="/" replace />;
  }

  return (
    <AuthProvider {...getOAuthConfig()} onSigninCallback={onAuthSignInCallback}>
      <CoreRouterContextConnector>
        <NoMatchingStateErrorWatcher />
        <RaygunTracker />
        <Suspense fallback={<PageLoader isLoading />}>
          <Outlet />
        </Suspense>
      </CoreRouterContextConnector>
    </AuthProvider>
  );
}
